// Copied from original/src/components/HelperComponents.tsx (ADR-08 §2.1): Logo (:18-74),
// ICON_COMPONENTS (:281-284), LoadingScreen (:286-306), DynamicIcon (:308-311),
// getIconContainerSizeClass (:313-321), getLucideIconSize (:323-331). TradingChart, TickerCard
// and WalletSelect (recharts / business data) are not extracted. Changes: `Logo` renders the
// Al-Faylasuf logo (original/public/icon.png, the old default `logoUrl: "./icon.png"`, App.tsx:2582)
// from a bundled asset; the office logo comes through the `logoUrl` prop; the "دقيق" assets are
// excluded (D-052); the loading text goes through t() (D-055).
import React from 'react';
import { motion } from 'motion/react';
import { 
  LayoutDashboard, ArrowLeftRight, FileText, MessageCircle, Plus, TrendingUp, Wallet, Search, Menu, X, Filter, ArrowUpDown, ArrowUp, ArrowDown, ArrowDownLeft, ArrowUpRight, Calendar, Settings, User, UserPlus, ShieldCheck, Palette, Languages, Grip, History, Printer, CheckCircle, Edit2, Trash2, Package, ShoppingCart, Store, TrendingDown, Zap, Sun, Moon, RefreshCw, Upload, Download, FolderOpen, Building2, Database, Shield, Layers, Calculator,
  Bell, CreditCard, PieChart, BarChart2, Activity, Globe, Lock, Key, Mail, Phone, MapPin, Clock, Info, AlertCircle, AlertTriangle, Check, ChevronRight, ChevronLeft, ChevronUp, ChevronDown, MoreHorizontal, MoreVertical, LogOut, LogIn, ExternalLink, Copy, Share2, Heart, Star, Bookmark, Hash, Tag, Image, Video, Music, Mic, Camera, Monitor, Smartphone, Tablet, Laptop, Cpu, HardDrive, MousePointer2, Type, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify, Link, Link2, Code, Terminal, Command, Box, Briefcase, GraduationCap, Trophy, Gift, Coffee, Utensils, Truck, Plane, Car, Bike, Map, Navigation, Compass, Anchor, LifeBuoy, Target, Crosshair, Eye, EyeOff, Ghost, Smile, Frown, Meh, Coins
} from 'lucide-react';
import { useT } from '../i18n-context.js';
import faylasufLogo from '../assets/faylasuf-logo.png';

export const DEFAULT_LOGO_URL = faylasufLogo;

export const Logo = ({ size = 40, className = "", logoUrl }: { size?: number, className?: string, logoUrl?: string | null }) => {
  const settings = { logoUrl: logoUrl === undefined ? faylasufLogo : logoUrl };
  
  if (settings.logoUrl) {
    return (
      <motion.div 
        whileHover={{ scale: 1.05 }}
        className={`relative flex items-center justify-center overflow-hidden rounded-2xl border border-white/20 dark:border-[#30363d] shadow-lg ${className}`} 
        style={{ width: size, height: size }}
      >
        <img 
          src={settings.logoUrl} 
          alt="Company Logo" 
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
        />
      </motion.div>
    );
  }

  return (
    <div className={`relative flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-900 rounded-2xl shadow-xl overflow-hidden"
        initial={{ rotate: 3 }}
        whileHover={{ rotate: 0, scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-accent-gold/20 to-transparent"></div>
      </motion.div>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-white relative z-10 drop-shadow-md" style={{ width: size * 0.6, height: size * 0.6 }}>
        <path d="M12 2C7.58 2 4 5.58 4 10c0 2.45 1.1 4.64 2.83 6.12L6 22l6-3 6 3-0.83-5.88C18.9 14.64 20 12.45 20 10c0-4.42-3.58-8-8-8z" />
        <path d="M12 6v4" />
        <path d="M9 10h6" />
        <circle cx="12" cy="10" r="1" fill="currentColor" />
      </svg>
      <motion.div 
        className="absolute -bottom-1 -right-1 bg-white dark:bg-gray-900 rounded-full p-1 shadow-lg border border-gray-100 dark:border-[#30363d]"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="bg-primary-600 rounded-full p-0.5">
          <Zap size={size * 0.25} className="text-white fill-white" />
        </div>
      </motion.div>
    </div>
  );
};

export const ICON_COMPONENTS: Record<string, any> = {
  LayoutDashboard, ArrowLeftRight, FileText, MessageCircle, Plus, TrendingUp, Wallet, Search, Menu, X, Filter, ArrowUpDown, ArrowUp, ArrowDown, ArrowDownLeft, ArrowUpRight, Calendar, Settings, User, UserPlus, ShieldCheck, Palette, Languages, Grip, History, Printer, CheckCircle, Edit2, Trash2, Package, ShoppingCart, Store, TrendingDown, Zap, Sun, Moon, RefreshCw, Upload, Download, FolderOpen, Building2, Database, Shield, Layers, Calculator,
  Bell, CreditCard, PieChart, BarChart2, Activity, Globe, Lock, Key, Mail, Phone, MapPin, Clock, Info, AlertCircle, AlertTriangle, Check, ChevronRight, ChevronLeft, ChevronUp, ChevronDown, MoreHorizontal, MoreVertical, LogOut, LogIn, ExternalLink, Copy, Share2, Heart, Star, Bookmark, Hash, Tag, Image, Video, Music, Mic, Camera, Monitor, Smartphone, Tablet, Laptop, Cpu, HardDrive, MousePointer2, Type, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify, Link, Link2, Code, Terminal, Command, Box, Briefcase, GraduationCap, Trophy, Gift, Coffee, Utensils, Truck, Plane, Car, Bike, Map, Navigation, Compass, Anchor, LifeBuoy, Target, Crosshair, Eye, EyeOff, Ghost, Smile, Frown, Meh
};

export const LoadingScreen = () => {
  const t = useT();
  return (
  <div className="fixed inset-0 bg-[#f8fafc] dark:bg-[#05070a] flex flex-col items-center justify-center z-[9999]">
    <div className="relative">
      <div className="w-24 h-24 border-4 border-blue-500/20 rounded-full animate-ping" />
      <div className="absolute inset-0 w-24 h-24 border-t-4 border-blue-600 rounded-full animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-12 h-12 bg-blue-600 rounded-2xl shadow-xl shadow-blue-500/40 animate-pulse flex items-center justify-center">
          <RefreshCw className="text-white animate-spin-slow" size={24} />
        </div>
      </div>
    </div>
    <div className="mt-12 text-center">
      <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tighter dark:digital-glow animate-pulse">
        {t('common.brand_latin')}
      </h2>
      <p className="mt-2 text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest text-[10px]">
        {t('common.loading')}
      </p>
    </div>
  </div>
  );
};

export const DynamicIcon = ({ name, size = 20, className = "" }: { name: string, size?: number, className?: string }) => {
  const IconComponent = ICON_COMPONENTS[name] || LayoutDashboard;
  return <IconComponent size={size} className={className} />;
};

export const getIconContainerSizeClass = (dSize: 'sm' | 'md' | 'lg' | 'xl') => {
  switch (dSize) {
    case 'sm': return 'w-11 h-11 rounded-xl';
    case 'md': return 'w-14 h-14 rounded-2xl';
    case 'lg': return 'w-16 h-16 rounded-2xl';
    case 'xl': return 'w-20 h-20 rounded-3xl';
    default: return 'w-14 h-14 rounded-2xl';
  }
};

export const getLucideIconSize = (dSize: 'sm' | 'md' | 'lg' | 'xl') => {
  switch (dSize) {
    case 'sm': return 18;
    case 'md': return 24;
    case 'lg': return 28;
    case 'xl': return 34;
    default: return 24;
  }
};
